import { ref, watch, type Ref } from 'vue'
import { jsonapiClient } from '@/api/client'
import { findResource, type Resource } from '@/api/resources'
import { latestCompletedEvaluation } from '@/lib/visitStates'

// The lead guard behind the create form: does the picked location already
// carry a LIVE lead — an open/assessed job with an evaluation visit? The two
// wrong turns a dispatcher can take from here (re-creating the same scope as a
// sibling job, or opening a second one without saying so) both get an honest
// door: « Continuer sur ce job » or « Nouveau job lié » (jobs.origin_job_id —
// the lead becomes the new job's parent, evaluation or not).
// Fail-soft by design: the guard must never block an intake, so any fetch
// error just clears it.
export function useLeadGuard(serviceLocationId: Ref<string | null>) {
  const leadJob = ref<Resource<'job'> | null>(null)

  watch(serviceLocationId, async (id) => {
    leadJob.value = null
    if (!id || id === '__new__') return

    try {
      const response = await jsonapiClient.get('/api/jobs', {
        params: {
          filter: { service_location_ids: [id], status: ['open', 'assessed'] },
          include: { visits: true },
          // The guard answers "is there a live lead here" — one small page is
          // the whole question; never pull a full enriched index page for it.
          page: { number: 1, size: 10 },
        },
      })
      if (serviceLocationId.value !== id) return // stale — the picker moved on

      const included = response.data.included ?? []
      const evaluationsOf = (job: Resource<'job'>) => {
        const rel = job.relationships?.visits?.data
        if (!Array.isArray(rel)) return []
        return rel
          .map(v => findResource(included, v.id, 'visit'))
          .filter((v): v is Resource<'visit'> => v !== null)
          .filter(v => v.attributes.kind === 'evaluation' && v.attributes.status !== 'cancelled')
      }

      // Prefer the lead whose look-see is COMPLETED (a child opened beside it
      // is born assessed); a lead with only a pending evaluation is the
      // fallback — never let the first pending lead mask an evaluated one.
      let fallback: Resource<'job'> | null = null
      for (const job of response.data.data) {
        const evaluations = evaluationsOf(job)
        if (!evaluations.length) continue
        if (latestCompletedEvaluation(evaluations)) {
          leadJob.value = job
          return
        }
        fallback ??= job
      }
      leadJob.value = fallback
    } catch {
      // fail-soft: a guard that errors is a guard that stays out of the way
    }
  }, { immediate: true })

  return { leadJob }
}
